import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canChangeStatus, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

export interface Distributor {
  id: string;
  businessName: string;
  contactName: string;
  documentId: string;
  phone: string;
  email: string;
  city: string;
  address: string;
  discountRate: number;
  creditStatus: string;
  createdAt: string;
}

export interface DistributorsResult {
  data: Distributor[];
}

export async function fetchDistributors(): Promise<DistributorsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const { data, error } = await supabase.from('distributors').select('*');

    if (error) {
      throw new Error(error.message);
    }

    const mapped: Distributor[] = ((data ?? []) as Array<Record<string, any>>)
      .map((r) => ({
        id: r.id,
        businessName: r.business_name,
        contactName: r.contact_name ?? '',
        documentId: r.document_id ?? '',
        phone: r.phone ?? '',
        email: r.email ?? '',
        city: r.city ?? '',
        address: r.address ?? '',
        discountRate: Number(r.discount_rate ?? 0),
        creditStatus: r.credit_status ?? '',
        createdAt: r.created_at ?? '',
      }))
      .sort((a, b) => (a.businessName < b.businessName ? -1 : 1));

    return { data: mapped };
  } catch (err) {
    console.error('[distributors] connection failed:', err);
    throw err;
  }
}

export interface DistributorInput {
  businessName: string;
  contactName: string;
  documentId: string;
  phone: string;
  email: string;
  city: string;
  address: string;
  discountRate: number;
  creditStatus: string;
}

export async function saveDistributor(
  input: DistributorInput,
): Promise<{ data: Distributor | null; error: string | null }> {
  if (!(await canWrite('distributors'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    business_name: input.businessName.trim(),
    contact_name: input.contactName.trim() || null,
    document_id: input.documentId.trim() || null,
    phone: input.phone.trim() || null,
    email: input.email.trim() || null,
    city: input.city.trim() || null,
    address: input.address.trim() || null,
    discount_rate: Number(input.discountRate) || 0,
    credit_status: input.creditStatus.trim() || null,
  };

  try {
    const { data, error } = await supabase.from('distributors').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer el distribuidor guardado.' };
    recordAudit({
      entityType: 'distributors',
      entityId: row.id,
      action: 'CREATE',
      newValue: input.businessName,
    });
    return {
      data: {
        id: row.id,
        businessName: row.business_name,
        contactName: row.contact_name ?? '',
        documentId: row.document_id ?? '',
        phone: row.phone ?? '',
        email: row.email ?? '',
        city: row.city ?? '',
        address: row.address ?? '',
        discountRate: Number(row.discount_rate ?? 0),
        creditStatus: row.credit_status ?? '',
        createdAt: row.created_at ?? '',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[distributors] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar el distribuidor.' };
  }
}

// Actualiza un distribuidor existente.
export async function updateDistributor(
  id: string,
  input: DistributorInput,
): Promise<{ data: Distributor | null; error: string | null }> {
  if (!(await canWrite('distributors'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    business_name: input.businessName.trim(),
    contact_name: input.contactName.trim() || null,
    document_id: input.documentId.trim() || null,
    phone: input.phone.trim() || null,
    email: input.email.trim() || null,
    city: input.city.trim() || null,
    address: input.address.trim() || null,
    discount_rate: Number(input.discountRate) || 0,
    credit_status: input.creditStatus.trim() || null,
  };

  try {
    const { data, error } = await supabase.from('distributors').update(payload).eq('id', id).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    recordAudit({
      entityType: 'distributors',
      entityId: id,
      action: 'UPDATE',
      newValue: input.businessName,
    });
    return {
      data: row
        ? {
            id: row.id,
            businessName: row.business_name,
            contactName: row.contact_name ?? '',
            documentId: row.document_id ?? '',
            phone: row.phone ?? '',
            email: row.email ?? '',
            city: row.city ?? '',
            address: row.address ?? '',
            discountRate: Number(row.discount_rate ?? 0),
            creditStatus: row.credit_status ?? '',
            createdAt: row.created_at ?? '',
          }
        : null,
      error: null,
    };
  } catch (err) {
    console.warn('[distributors] update failed:', err);
    return { data: null, error: 'Error de conexión al actualizar el distribuidor.' };
  }
}

// Cambia el estado de crédito de un distribuidor (Al día / Pendiente de pago)
// dejando registro en auditoría.
export async function updateDistributorCreditStatus(
  id: string,
  creditStatus: string,
): Promise<{ error: string | null }> {
  if (!(await canChangeStatus('distributors'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { data: current } = await supabase
    .from('distributors')
    .select('credit_status')
    .eq('id', id)
    .maybeSingle();

  const { error } = await supabase
    .from('distributors')
    .update({ credit_status: creditStatus })
    .eq('id', id);
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'distributors',
    entityId: id,
    action: 'UPDATE',
    fieldName: 'credit_status',
    oldValue: (current as Record<string, any> | null)?.credit_status ?? '',
    newValue: creditStatus,
  });
  return { error: null };
}